import { TaskSidenavService } from '@shared/modules/task-sidenav/services/task-sidenav.service';
import { TASK_SIDENAV_FORM } from '@shared/modules/task-sidenav/const/task-sidenav-form';
import { HttpError } from '@shared/interfaces/error.interface';
import { SnackBarService } from '@core/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { Config } from '@shared/configs/config';
import { Task } from '@shared/models/task.model';
import { SubtasksService } from '@shared/modules/subtasks/subtasks.service';
import { TaskStatus } from '@shared/enums/task-status.enum';
import { ObjectTaskType } from '@shared/enums/task-type.enum';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { ListTaskEventType, ListTaskService } from '@shared/modules/list/services/list-task.service';
import { map, tap } from 'rxjs/operators';
import { filterAndMapErrorMessages } from '@shared/helpers/filter-and-map-error.helper';
import { WindowHelper } from '@shared/helpers/window.helper';
import { InputV2Component } from '@shared/modules/forms-v2/components/input-v2/input-v2.component';
import { EmployeeSearchComponent } from '@shared/components/employee-search/employee-search.component';
import { DateRangePickerV2Component } from '@shared/components/date-rangepicker-v2/date-rangepicker-v2.component';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { formatApiUtcDateTime } from '@shared/helpers/date-format.helper';

@Component({
  selector: 'subtask-create',
  templateUrl: './subtask-create.component.html',
  styleUrls: ['./subtask-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubtaskCreateComponent implements OnInit, AfterViewInit {
  ButtonTypes = ButtonTypes;
  ButtonSize = ButtonSize;
  TASK_SIDENAV_FORM = TASK_SIDENAV_FORM;
  WindowHelper = WindowHelper;

  private readonly dialog = inject(MatDialog);

  loading: boolean = false;

  @ViewChild('employeeSearch') employeeSearch: EmployeeSearchComponent;
  @ViewChild('datepicker') datepicker: DateRangePickerV2Component;
  @ViewChild('taskTitle') taskTitle: InputV2Component;
  @Output() discard: EventEmitter<boolean> = new EventEmitter();

  @Input() parentId: number;

  form: UntypedFormGroup;

  get initialFormValues() {
    return {
      [TASK_SIDENAV_FORM.description]: null,
      [TASK_SIDENAV_FORM.assignedTo]: [],
      [TASK_SIDENAV_FORM.termEnd]: null,
      [TASK_SIDENAV_FORM.termStart]: null,
      parentId: null,
      objectType: ObjectTaskType.PLAIN,
      [TASK_SIDENAV_FORM.type]: this.service.type,
      [TASK_SIDENAV_FORM.status]: TaskStatus.CREATED
    };
  }

  get errorMessages() {
    const messages = Config.validationMessages;
    const control = this.form.get(TASK_SIDENAV_FORM.description);

    return filterAndMapErrorMessages(messages, control.errors);
  }

  constructor(
    private fb: UntypedFormBuilder,
    private changes: ChangeDetectorRef,
    public taskService: TaskSidenavService,
    private service: SubtasksService,
    private listTaskService: ListTaskService,
    private t: TranslateService,
    private s: SnackBarService
  ) {}

  ngOnInit() {
    this.createForm();
  }

  ngAfterViewInit() {
    this.taskTitle.inputv2.nativeElement.focus();
  }

  createForm() {
    this.form = this.fb.group(this.initialFormValues);
    this.form.get(TASK_SIDENAV_FORM.description).setValidators([Validators.required]);
    this.form.get(TASK_SIDENAV_FORM.description).updateValueAndValidity();
  }

  resetDefault() {
    this.form.setValue(this.initialFormValues);
    this.form.get(TASK_SIDENAV_FORM.description).markAsUntouched();
    this.form.get(TASK_SIDENAV_FORM.description).updateValueAndValidity();
    this.form.get(TASK_SIDENAV_FORM.assignedTo).markAsUntouched();
    this.form.get(TASK_SIDENAV_FORM.assignedTo).updateValueAndValidity();
    this.form.updateValueAndValidity();
    this.datepicker?.resetValue();
    this.form.markAsPristine();
  }

  discardAdding(): void {
    this.dialog
      .open(ConfirmModalComponent, {
        width: Config.CONFIRM_MODAL_WIDTH,
        autoFocus: false,
        data: {
          title: 'Tasks.acceptDiscardSubtask',
          body: this.t.instant('Tasks.acceptDiscardSubtaskText'),
          submitButtonText: 'Tasks.accept',
          submitButtonType: ButtonTypes.NEW_RED,
          cancelButtonText: 'Tasks.discard'
        }
      })
      .afterClosed()
      .pipe(
        tap((confirmed: boolean) => {
          if (confirmed) {
            this.resetDefault();
            this.discard.emit(true);
            this.changes.detectChanges();
          }
        })
      )
      .subscribe();
  }

  setDataBeforeSave() {
    this.form.get('parentId').setValue(this.service.parentId);
    const value = Object.assign(this.form.value, {});
    value.dataChanges = 1;
    delete value.assignedToIds;
    return Object.assign(value, this.employeeSearch.formData);
  }

  saveSubtask() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.changes.detectChanges();
    if (this.loading || this.form.invalid) return;
    this.loading = true;
    const form = this.setDataBeforeSave();

    if (this.parentId) {
      this.service
        .createTask(form)
        .pipe(map((task) => new Task(task)))
        .subscribe({ next: this.successAddSubtask.bind(this), error: this.errorAddSubtask.bind(this) })
        .add(() => {
          this.loading = false;
          this.changes.detectChanges();
        });
    } else {
      this.service.addTempSubtask(form);
      this.resetDefault();
      this.loading = false;
    }

    this.changes.detectChanges();
  }

  successAddSubtask(task: Task) {
    this.service.taskAdd.emit(task);
    this.s.success(this.t.instant('Tasks.successSubtask'));
    this.resetDefault();
    this.taskTitle.inputv2.nativeElement.focus();
    this.listTaskService.emitter.emit({ type: ListTaskEventType.ADD_SUB_TASK, data: task });
  }

  errorAddSubtask(err: HttpError) {
    this.s.error(this.t.instant('Tasks.errorSubtask'));
  }

  rangeChanged($event) {
    const startControl = this.form.get(TASK_SIDENAV_FORM.termStart);
    const endControl = this.form.get(TASK_SIDENAV_FORM.termEnd);

    startControl.setValue(formatApiUtcDateTime($event.start));
    endControl.setValue(formatApiUtcDateTime($event.end, { endOfDay: true }));
    this.form.markAsDirty();
    this.changes.detectChanges();
  }

  @HostListener('document:keyup', ['$event']) onKeyupHandler(e: KeyboardEvent) {
    e.keyCode === 13 ? this.saveSubtask() : null;
  }
}
